import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Props } from '../HereMap'
import { defaultLocation, defaultZoom } from '../defaults'
import { EntityLocationDTO } from '@user/store/map/typings'
import { selectContactLocations } from '@user/store/map'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'

type LatLong = {
  lat: number
  lng: number
}
export type PendingCustomReason = {
  pinId: number
  reason: string | null
}

function useData(props: Props) {
  const { center: centerProp, zoom: zoomProp } = props

  const [locationResolved, setLocationResolved] = useState<boolean>(false)

  const [initSource, setInitSource] = useState<
    'props' | 'localStorage' | 'browserLocation' | null
  >(null)

  const [center, setCenter] = useState<H.geo.IPoint>()
  const [zoom, setZoom] = useState<number>()
  const [hoveredPin, setHoveredPin] = useState<EntityLocationDTO | null>(null)
  const [clickedPin, setClickedPin] = useState<EntityLocationDTO | null>(null)
  const [tooltipHovered, setTooltipHovered] = useState(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [multiTenantDrawerOpen, setMultiTenantDrawerOpen] = useState(false)
  const [foundLocationFromSearch, setFoundLocationFromSearch] = useState(false)
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false)
  const [openKnockStatusModal, setOpenKnockStatusModal] = useState(false)
  const [addressDetailsForAddLead, setAddressDetailsForAddLead] =
    useState<HereMapClickEvent | null>(null)
  const [addressDetailsForAddLeadTemp, setAddressDetailsForAddLeadTemp] =
    useState<HereMapClickEvent | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [showHistory, setShowHistory] = useState(false)
  const [knockStatusModalOpen, setKnockStatusModalOpen] = useState(false)
  const [addressDetailsForKnockStatus, setAddressDetailsForKnockStatus] =
    useState<EntityLocationDTO | null>(null)
  const [
    addressDetailsForKnockStatusTemp,
    setAddressDetailsForKnockStatusTemp,
  ] = useState<HereMapClickEvent | null>(null)

  const [pendingCustomReason, setPendingCustomReason] =
    useState<PendingCustomReason | null>(null)

  const contactLocations = useSelector(selectContactLocations)

  useEffect(() => {
    if (!clickedPin) return
    const updated = contactLocations.find((p) => p.pinId === clickedPin.pinId)
    if (updated !== undefined && updated !== clickedPin) {
      setClickedPin(updated)
    }
  }, [contactLocations, clickedPin])

  function setDefaultLocation() {
    setCenter(defaultLocation)
    setZoom(defaultZoom)
  }

  useEffect(() => {
    if (
      props.center != null &&
      props.center.lat != null &&
      props.center.lng != null &&
      props.zoom != null
    ) {
      setInitSource('props')
      return
    }
    const localStorageCenter = localStorage.getItem('mapCenter')
    const localStorageZoom = localStorage.getItem('mapZoom')
    if (localStorageCenter && localStorageZoom) {
      setInitSource('localStorage')
      return
    }

    setInitSource('browserLocation')
  }, [props.center, props.zoom])

  useEffect(() => {
    if (!initSource) return

    setLocationResolved(false)

    switch (initSource) {
      case 'props':
        setCenter(
          centerProp ? { lat: centerProp.lat, lng: centerProp.lng } : undefined,
        )
        setZoom(zoomProp)
        break
      case 'localStorage': {
        const localStorageCenter = localStorage.getItem('mapCenter')
        const localStorageZoom = localStorage.getItem('mapZoom')
        if (localStorageCenter && localStorageZoom) {
          try {
            const parsedCenter: LatLong = JSON.parse(localStorageCenter)
            setCenter({ lat: parsedCenter.lat, lng: parsedCenter.lng })
            setZoom(Number(localStorageZoom))
          } catch (e) {
            setDefaultLocation()
          }
        }
        break
      }
      case 'browserLocation': {
        if (navigator.geolocation) {
          let cancelled = false
          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (cancelled) return
              setCenter({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              })
              setZoom(defaultZoom)
              setLocationResolved(true)
            },
            () => {
              if (cancelled) return
              setCenter(defaultLocation)
              setZoom(defaultZoom)
              setLocationResolved(true)
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            },
          )
          return () => {
            cancelled = true
          }
        }
        setDefaultLocation()
        return
      }
      default:
        setDefaultLocation()
        break
    }

    setLocationResolved(true)
  }, [initSource])

  const triggerRefresh = useCallback(
    () => setRefreshTrigger((prev) => prev + 1),
    [],
  )

  const convertHereCoordsToEntityLocation = useCallback(
    (location: HereMapClickEvent): EntityLocationDTO => {
      const street1 =
        location.houseNumber && location.street
          ? `${location.houseNumber} ${location.street}`
          : location.street || location.address?.street1 || ''

      return {
        entityId: 0,
        entityType: location.entityType ?? 'Unknown',
        latitude: location.latitude,
        longitude: location.longitude,
        serviceLocationId: 0,
        multitenant: false,
        pinId: '',
        count: 0,
        entities: [],
        formattedAddress: location.label || '',
        street: location.street,
        houseNumber: location.houseNumber,
        street1,
        street2: '',
        city: location.city || '',
        stateAbbreviation: location.state || '',
        postalCode: location.postalCode || '',
        country: '',
        county: location.county || '',
        boundingBox: undefined,
        assigneeId: undefined,
        knockStatusLocationId: location.knockStatusLocationId,
        contactLocationId: location.contactLocationId,
        knockStatusCreatedTime: location.knockStatusCreatedTime,
        knockStatusCreatedByName: location.knockStatusCreatedByName,
        knockStatusCount: location.knockStatusCount,
        knockStatusHistory: location.knockStatusHistory,
      }
    },
    [],
  )

  const convertEntityLocationToHereEvent = useCallback(
    (location: EntityLocationDTO): HereMapClickEvent => {
      // Tile cache (MapPinDTO) omits the `address` sub-object; fall back to
      // the top-level fields that are always present on both DTO shapes.
      const effectiveAddress = location.address ?? {
        street1: location.street1,
        street2: location.street2,
        city: location.city,
        stateAbbreviation: location.stateAbbreviation,
        postalCode: location.postalCode,
        country: location.country,
        latitude: location.latitude,
        longitude: location.longitude,
        knockStatusLocationId: location.knockStatusLocationId ?? undefined,
      }

      const street = effectiveAddress.street1 || ''
      const match = street.match(/^(\d+)\s*(.*)$/)
      const houseNumber = match ? match[1] : ''
      const streetName = match ? match[2] : street

      return {
        houseNumber,
        street: streetName,
        city: effectiveAddress.city,
        state: effectiveAddress.stateAbbreviation,
        postalCode: effectiveAddress.postalCode,
        latitude: effectiveAddress.latitude ?? location.latitude,
        longitude: effectiveAddress.longitude ?? location.longitude,
        entityType: location.entityType,
        knockStatusLocationId: location.knockStatusLocationId,
        contactLocationId: location.contactLocationId,
        knockStatusCreatedTime: location.knockStatusCreatedTime,
        knockStatusCreatedByName: location.knockStatusCreatedByName,
        knockStatusCount: location.knockStatusCount,
        knockStatusHistory: location.knockStatusHistory,
        address: effectiveAddress,
        knockStatusReason: location.knockStatusReason,
        knockStatusSettingName: location.knockStatusSettingName,
        pestPacDetails: location.pestPacDetails ?? null,
      }
    },
    [],
  )

  return {
    locationResolved,
    center,
    zoom,
    hoveredPin,
    setHoveredPin,
    clickedPin,
    setClickedPin,
    tooltipHovered,
    setTooltipHovered,
    drawerOpen,
    setDrawerOpen,
    multiTenantDrawerOpen,
    setMultiTenantDrawerOpen,
    foundLocationFromSearch,
    setFoundLocationFromSearch,
    addressDetailsForAddLead,
    setAddressDetailsForAddLead,
    addLeadModalOpen,
    setAddLeadModalOpen,
    openKnockStatusModal,
    setOpenKnockStatusModal,
    refreshTrigger,
    triggerRefresh,
    addressDetailsForAddLeadTemp,
    setAddressDetailsForAddLeadTemp,
    convertHereCoordsToEntityLocation,
    knockStatusModalOpen,
    setKnockStatusModalOpen,
    addressDetailsForKnockStatus,
    setAddressDetailsForKnockStatus,
    addressDetailsForKnockStatusTemp,
    setAddressDetailsForKnockStatusTemp,
    showHistory,
    setShowHistory,
    pendingCustomReason,
    setPendingCustomReason,
    convertEntityLocationToHereEvent,
  }
}

export { useData, LatLong }
